/**
 * PMGT FE-2 — the unified project wizard entry point.
 *
 * One component, two modes (PM-D confirmed): **create** and **edit** share the
 * SAME stepper flow — Basics → Parties → Parks & turbines → Review/Overview —
 * where the Overview tab IS the Review step. The two orchestrators own
 * genuinely different lifecycles (create mints the whole hierarchy on submit;
 * edit loads a live project and PATCHes / adds / removes incrementally), so
 * they are split into `CreateModeWizard` / `EditModeWizard`. Both render through
 * the shared `WizardShell` + shared Basics/Parties step bodies, so the flow
 * indication is identical.
 *
 * Splitting by mode (rather than branching inside one component) keeps each
 * mode's hooks isolated: the create flow never calls the edit-only hooks
 * (`useProjectDetailQuery`, `useUpdateProjectMutation`, the DELETE mutations),
 * so the existing create tests — which mock only the create hook surface — keep
 * passing unchanged.
 */
import { type ReactElement } from "react";
import { ToastProvider } from "../../account/ToastContext.js";
import { CreateModeWizard } from "./CreateModeWizard.js";
import { EditModeWizard } from "./EditModeWizard.js";

export type ProjectWizardProps =
  | { mode: "create" }
  | {
      mode: "edit";
      projectId: string;
      isOwnerProject?: boolean;
      /** DL-01 — the caller's access role on a MEMBER project (the list row's
       *  `role`); the entity tree offers "Download images" to a customer only
       *  with it + the owner's flag. Null / absent on an own project. */
      accessRole?: string | null;
    };

export function ProjectWizard(props: ProjectWizardProps): ReactElement {
  return (
    <ToastProvider>
      {props.mode === "create" ? (
        <CreateModeWizard />
      ) : (
        <EditModeWizard
          projectId={props.projectId}
          isOwnerProject={props.isOwnerProject ?? true}
          accessRole={props.accessRole ?? null}
        />
      )}
    </ToastProvider>
  );
}
